import { Box } from "@mui/material"
import type { FC } from "react"
import { useEffect } from "react"
import { useFormContext } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { FormTextField } from "@/components/UiKit"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import CompanyTypeSelect from "@/screens/Companies/components/CompanyTypeSelect"
import PreferredMeasuringSystemSelects from "@/screens/Companies/components/PreferredMeasuringSystemSelects"
import type { TreeNode } from "@/types/tree"

interface Props {
  companyTypeList: TreeNode<string>[]
  handleCancel: () => void
  onSubmit: () => void
}

export const EditCompanyForm: FC<Props> = ({ companyTypeList, handleCancel, onSubmit }) => {
  const {
    control,
    watch,
    clearErrors,
    formState: { isValid, isDirty },
  } = useFormContext()
  const { hasPermissions } = usePermissionManager()

  // AK-6794: activation status no longer gates Company Type edits — permission does.
  const canEditCompanyType = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const types = watch("types")

  useEffect(() => {
    clearErrors("name")
  }, [types, clearErrors])

  return (
    <form onSubmit={onSubmit} className='space-y-8 p-10'>
      <Box>
        <FormTextField control={control} name='name' label='Company Name' placeholder='Company Name' required />
      </Box>
      <Box>
        <CompanyTypeSelect companyTypeTree={companyTypeList} name='types' disabled={!canEditCompanyType} />

        <PreferredMeasuringSystemSelects />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <Button appearance='outlined' onClick={handleCancel}>
          Cancel
        </Button>
        <Button appearance='contained' type='submit' disabled={!isValid || !isDirty}>
          Submit
        </Button>
      </Box>
    </form>
  )
}
